<template>
  <b-modal
    v-model="showModal"
    title="Positive Identification Required"
    :busy="busy"
    ok-title="Verify"
    cancel-title="Cancel"
    ok-variant="primary"
    :ok-disabled="!canSubmit"
    centered
    @ok.prevent="onVerify"
    @cancel="onCancel"
    @hidden="onHidden"
  >
    <p class="mb-3">
      This action requires a second identification factor in addition to your login.
    </p>

    <div v-if="badgeAllowed" class="mb-3">
      <label class="pi-label">Badge</label>
      <div class="pi-reader-status" :class="readerStatusClass">
        <span class="pi-dot"></span>
        <span>{{ badgeStatusText }}</span>
      </div>
      <div v-if="badgeEvidence" class="pi-captured">Badge captured.</div>
      <b-form-checkbox v-model="manualEntry" class="mt-2">Enter badge ID manually</b-form-checkbox>
      <b-form-input
        v-if="manualEntry"
        v-model="manualBadge"
        class="mt-2"
        placeholder="Badge ID"
        autocomplete="off"
      />
    </div>

    <div v-if="biometricAllowed" class="mb-3">
      <label class="pi-label">Fingerprint</label>
      <div class="pi-reader-status" :class="readerStatusClass">
        <span class="pi-dot"></span>
        <span>{{ fingerprintStatusText }}</span>
      </div>
      <div v-if="fingerprintEvidence" class="pi-captured">
        <i class="fas fa-fingerprint mr-1" /> Fingerprint captured.
      </div>
    </div>

    <div v-if="!badgeAllowed && !biometricAllowed" class="text-warning">
      No positive-identification methods are enabled in Configuration.
    </div>

    <b-alert :show="!!error" variant="danger" class="mt-3 mb-0">{{ error }}</b-alert>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as signalR from '@microsoft/signalr';
import { buildClientServiceHub, BadgeTapEvent, FingerprintCapturedEvent } from '@/services/ClientServiceHub';
import PositiveIdService, {
  PositiveIdActionType,
  PositiveIdTargetEntityType,
  PositiveIdMethod,
} from '@/services/PositiveIdService';

@Component
export default class PositiveIdModal extends Vue {
  @Prop({ type: Boolean, default: false }) readonly show!: boolean;
  @Prop({ type: Number, required: true }) readonly actionType!: PositiveIdActionType;
  @Prop({ type: Number, required: true }) readonly targetEntityType!: PositiveIdTargetEntityType;
  @Prop({ type: String, required: true }) readonly targetEntityId!: string;
  // Methods enabled in Configuration, e.g. ["badge", "biometric"]. Empty/null => badge allowed.
  @Prop({ type: Array, default: null }) readonly enabledMethods!: string[] | null;

  private showModal = false;
  private busy = false;
  private error = '';
  private badgeEvidence = '';
  private manualEntry = false;
  private manualBadge = '';
  private fingerprintEvidence = '';
  private readerStatus: 'idle' | 'connecting' | 'connected' | 'unavailable' = 'idle';
  private hub: signalR.HubConnection | null = null;
  private resolutionEmitted = false;

  private get badgeAllowed(): boolean {
    if (!this.enabledMethods || this.enabledMethods.length === 0) return true;
    return this.enabledMethods.includes('badge');
  }

  private get biometricAllowed(): boolean {
    return !!this.enabledMethods && this.enabledMethods.includes('biometric');
  }

  private get effectiveBadgeEvidence(): string {
    return this.manualEntry ? this.manualBadge.trim() : this.badgeEvidence;
  }

  // Fingerprint takes precedence when both are captured — biometric is the stronger factor.
  private get chosenMethod(): PositiveIdMethod | null {
    if (this.fingerprintEvidence) return PositiveIdMethod.Biometric;
    if (this.effectiveBadgeEvidence) return PositiveIdMethod.ProximityBadge;
    return null;
  }

  private get chosenEvidence(): string {
    if (this.fingerprintEvidence) return this.fingerprintEvidence;
    return this.effectiveBadgeEvidence;
  }

  private get canSubmit(): boolean {
    return !this.busy && this.chosenMethod !== null;
  }

  private get readerStatusClass(): string {
    return `pi-status-${this.readerStatus}`;
  }

  private get badgeStatusText(): string {
    switch (this.readerStatus) {
      case 'connecting': return 'Connecting to reader…';
      case 'connected': return this.badgeEvidence ? 'Badge read.' : 'Tap your badge…';
      case 'unavailable': return 'Badge reader unavailable — enter badge ID manually.';
      default: return '';
    }
  }

  private get fingerprintStatusText(): string {
    switch (this.readerStatus) {
      case 'connecting': return 'Connecting to reader…';
      case 'connected': return this.fingerprintEvidence ? 'Fingerprint read.' : 'Place finger on the reader…';
      case 'unavailable': return 'Fingerprint reader unavailable.';
      default: return '';
    }
  }

  @Watch('show', { immediate: true })
  async onShowChanged(value: boolean): Promise<void> {
    this.showModal = value;
    if (value) {
      this.reset();
      this.resolutionEmitted = false;
      if (this.badgeAllowed || this.biometricAllowed) await this.connectHub();
    } else {
      await this.disconnectHub();
    }
  }

  @Watch('showModal')
  onShowModalChanged(value: boolean): void {
    if (!value && value !== this.show) this.$emit('update:show', false);
  }

  private reset(): void {
    this.error = '';
    this.badgeEvidence = '';
    this.manualEntry = false;
    this.manualBadge = '';
    this.fingerprintEvidence = '';
    this.readerStatus = 'idle';
  }

  private async connectHub(): Promise<void> {
    if (this.hub) return;
    this.readerStatus = 'connecting';
    try {
      this.hub = buildClientServiceHub();
      if (this.badgeAllowed) {
        this.hub.on('BadgeTap', (evt: BadgeTapEvent) => {
          this.badgeEvidence = evt?.cardDataHex ?? evt?.CardDataHex ?? '';
        });
      }
      if (this.biometricAllowed) {
        this.hub.on('FingerprintCaptured', (evt: FingerprintCapturedEvent) => {
          this.fingerprintEvidence = evt?.templateBase64 ?? evt?.TemplateBase64 ?? '';
        });
      }
      await this.hub.start();
      this.readerStatus = 'connected';
    } catch {
      // Local client service / readers not reachable — fall back to manual badge entry if allowed.
      this.readerStatus = 'unavailable';
      if (this.badgeAllowed) this.manualEntry = true;
      this.hub = null;
    }
  }

  private async disconnectHub(): Promise<void> {
    if (!this.hub) return;
    try { await this.hub.stop(); } catch { /* ignore */ }
    this.hub = null;
  }

  async onVerify(): Promise<void> {
    if (!this.canSubmit || this.chosenMethod === null) return;
    this.busy = true;
    this.error = '';
    try {
      const challenge = await PositiveIdService.challenge({
        actionType: this.actionType,
        targetEntityType: this.targetEntityType,
        targetEntityId: this.targetEntityId,
      });
      const verified = await PositiveIdService.verify({
        challengeToken: challenge.challengeToken,
        method: this.chosenMethod,
        evidence: this.chosenEvidence,
      });
      this.resolutionEmitted = true;
      this.$emit('verified', { verifiedToken: verified.verifiedToken });
      this.showModal = false;
    } catch (err: any) {
      this.error = err?.response?.data ?? 'Positive identification failed. Please try again.';
    } finally {
      this.busy = false;
    }
  }

  onCancel(): void {
    this.resolutionEmitted = true;
    this.$emit('cancelled');
  }

  async onHidden(): Promise<void> {
    await this.disconnectHub();
    if (!this.resolutionEmitted) {
      this.$emit('cancelled');
    }
  }

  beforeDestroy(): void {
    void this.disconnectHub();
  }
}
</script>

<style scoped>
.pi-label {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}

.pi-reader-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.pi-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #adb5bd;
}

.pi-status-connecting .pi-dot { background: #ffc107; }
.pi-status-connected .pi-dot { background: #28a745; }
.pi-status-unavailable .pi-dot { background: #dc3545; }

.pi-captured {
  color: #28a745;
  font-weight: 600;
  margin-top: 6px;
}
</style>
